<script setup lang="ts">
import type { QuoteSubcontracting, SubcontractMode, Supplier } from '@cockpit/shared';
import { computed, onMounted, ref, watch } from 'vue';
import { subcontractingApi } from '../../api/subcontracting';
import { errorText } from '../../composables/useResource';
import { eur } from '../../format';
import { parseEurosInput } from '../../expenses';
import { centsInput } from '../../quoteDraft';
import { useOfflineStore } from '../../stores/offline';
import { useToastStore } from '../../stores/toast';
import {
  bodyFromForm,
  checkLine,
  dueExplanation,
  formFromDto,
  formTotals,
  frDate,
  supplierOptions,
  type SubcontractForm,
} from '../../subcontracting';
import SearchSelect from '../SearchSelect.vue';
import '../../styles/subcontracting.css';

/**
 * Lot AY « Sous-traitance » (maquette, onglet « Préparer le projet », repères 1 à 6) : les lignes du devis, à cocher
 * quand elles sont sous-traitées (sous-traitant = fournisseur Axonaut, coût prérempli avec le prix de la ligne), ou
 * un montant global, ou « Aucune ». Part d'Evolusite en direct, date « à payer au plus tard » et « Changer la date ».
 * Utilisé dans « Préparer le projet », la page client et la reprise. Enregistrement interne et immédiat (rien ne part
 * vers Axonaut ni vers le sous-traitant), réservé au réseau.
 */
const props = defineProps<{ quoteId: string; compact?: boolean }>();
const emit = defineEmits<{ saved: [value: QuoteSubcontracting] }>();

const toast = useToastStore();
const offline = useOfflineStore();
const online = computed(() => offline.online);

const dto = ref<QuoteSubcontracting | null>(null);
const suppliers = ref<Supplier[]>([]);
const form = ref<SubcontractForm | null>(null);
const costTexts = ref<Record<string, string>>({});
const globalText = ref('');
const loadError = ref('');
const saving = ref(false);
const error = ref('');
const editingDate = ref(false);
const dateInput = ref('');

const options = computed(() => supplierOptions(suppliers.value));
const totals = computed(() => (dto.value && form.value ? formTotals(dto.value, form.value) : null));
const savedSubcontracted = computed(() => (dto.value?.subcontractedCents ?? 0) > 0);
const mainSupplier = computed(() => {
  const d = dto.value;
  if (!d) return null;
  return d.lines.find((l) => l.subcontract)?.subcontract?.supplierName ?? d.global?.supplierName ?? null;
});

function reset(value: QuoteSubcontracting): void {
  dto.value = value;
  form.value = formFromDto(value);
  costTexts.value = Object.fromEntries(
    Object.entries(form.value.lines).map(([key, l]) => [
      key,
      l.costCents !== null ? centsInput(l.costCents) : '',
    ]),
  );
  globalText.value = form.value.global.costCents !== null ? centsInput(form.value.global.costCents) : '';
}

async function load(): Promise<void> {
  loadError.value = '';
  try {
    const [value, list] = await Promise.all([
      subcontractingApi.quote(props.quoteId),
      subcontractingApi.suppliers(),
    ]);
    suppliers.value = list.items;
    reset(value);
  } catch (err) {
    loadError.value = errorText(err);
  }
}
onMounted(load);
watch(() => props.quoteId, load);

function setMode(mode: SubcontractMode): void {
  if (form.value) form.value = { ...form.value, mode };
}
function toggleLine(key: string, checked: boolean, amountCents: number): void {
  if (!form.value) return;
  form.value = checkLine(form.value, key, checked, amountCents);
  const cost = form.value.lines[key]?.costCents;
  if (checked && cost !== null && cost !== undefined)
    costTexts.value = { ...costTexts.value, [key]: centsInput(cost) };
}
function setLineSupplier(key: string, supplierId: string | null): void {
  if (!form.value) return;
  const line = form.value.lines[key];
  if (line) form.value = { ...form.value, lines: { ...form.value.lines, [key]: { ...line, supplierId } } };
}
function setLineCost(key: string, text: string): void {
  if (!form.value) return;
  costTexts.value = { ...costTexts.value, [key]: text };
  const parsed = parseEurosInput(text);
  const line = form.value.lines[key];
  if (line)
    form.value = {
      ...form.value,
      lines: {
        ...form.value.lines,
        [key]: { ...line, costCents: parsed === undefined ? null : (parsed ?? null) },
      },
    };
}
function setGlobalSupplier(supplierId: string | null): void {
  if (form.value) form.value = { ...form.value, global: { ...form.value.global, supplierId } };
}
function setGlobalCost(text: string): void {
  if (!form.value) return;
  globalText.value = text;
  const parsed = parseEurosInput(text);
  form.value = {
    ...form.value,
    global: { ...form.value.global, costCents: parsed === undefined ? null : (parsed ?? null) },
  };
}

async function save(): Promise<void> {
  const d = dto.value;
  const f = form.value;
  if (!d || !f || !totals.value) return;
  if (totals.value.missing) {
    error.value = totals.value.missing;
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const value = await subcontractingApi.saveQuote(d.quoteId, bodyFromForm(d, f));
    reset(value);
    emit('saved', value);
    toast.show(
      value.status === 'none'
        ? 'Aucune sous-traitance : le devis compte en entier pour Evolusite.'
        : `Sous-traitance enregistrée : ${eur(value.evolusiteCents)} pour Evolusite.`,
    );
  } catch (err) {
    error.value = errorText(err);
  } finally {
    saving.value = false;
  }
}

function openDate(): void {
  dateInput.value = dto.value?.due.date ?? '';
  editingDate.value = true;
}
async function saveDate(date: string | null): Promise<void> {
  const d = dto.value;
  if (!d) return;
  saving.value = true;
  error.value = '';
  try {
    const value = await subcontractingApi.setDueDate(d.quoteId, date);
    reset(value);
    emit('saved', value);
    editingDate.value = false;
    toast.show(value.due.date ? `À payer au plus tard le ${frDate(value.due.date)}.` : 'Date recalculée.');
  } catch (err) {
    error.value = errorText(err);
  } finally {
    saving.value = false;
  }
}

const barStyle = computed(() => {
  const d = dto.value;
  const t = totals.value;
  if (!d || !t || d.amountCents <= 0) return { ev: '100%', st: '0%' };
  const st = Math.min(100, (t.subcontractedCents / d.amountCents) * 100);
  return { ev: `${100 - st}%`, st: `${st}%` };
});
</script>

<template>
  <div class="st-block" data-testid="subcontracting-block">
    <p v-if="loadError" class="form-error" role="alert">
      {{ loadError }} <button class="btn sm" type="button" @click="load">Réessayer</button>
    </p>
    <p v-else-if="!dto || !form" class="muted" aria-busy="true">Chargement de la sous-traitance…</p>
    <template v-else>
      <div class="st-head">
        <div>
          <h3>Sous-traitance</h3>
          <p>
            Devis {{ dto.number ?? 'sans numéro'
            }}<template v-if="dto.clientName"> · {{ dto.clientName }}</template
            ><template v-if="dto.signedOn"> · signé le {{ frDate(dto.signedOn) }}</template> ·
            {{ eur(dto.amountCents) }} HT
          </p>
        </div>
        <div class="seg" role="group" aria-label="Sous-traitance du devis">
          <button
            type="button"
            :aria-pressed="form.mode === 'lines'"
            data-testid="st-mode-lines"
            @click="setMode('lines')"
          >
            Par ligne
          </button>
          <button
            type="button"
            :aria-pressed="form.mode === 'global'"
            data-testid="st-mode-global"
            @click="setMode('global')"
          >
            Montant global
          </button>
          <button
            type="button"
            :aria-pressed="form.mode === 'none'"
            data-testid="st-mode-none"
            @click="setMode('none')"
          >
            Aucune
          </button>
        </div>
      </div>

      <div v-if="form.mode === 'lines'" class="st-lines" data-testid="st-lines">
        <p v-if="dto.lines.length === 0" class="muted">
          Ce devis n’a pas de lignes dans la copie d’Axonaut : choisissez « Montant global ».
        </p>
        <div
          v-for="line in dto.lines"
          :key="line.key"
          class="st-line"
          :class="{ on: form.lines[line.key]?.checked }"
          :data-testid="`st-line-${line.index}`"
        >
          <input
            :id="`st-line-${quoteId}-${line.index}`"
            type="checkbox"
            :checked="form.lines[line.key]?.checked ?? false"
            :aria-label="`Sous-traiter « ${line.label} »`"
            @change="toggleLine(line.key, ($event.target as HTMLInputElement).checked, line.amountCents)"
          />
          <label class="nm" :for="`st-line-${quoteId}-${line.index}`">{{ line.label }}</label>
          <span class="pr">{{ eur(line.amountCents) }}</span>
          <span class="det">
            <template v-if="form.lines[line.key]?.checked">
              <span class="sub-chip">Sous-traité</span>
              <SearchSelect
                class="ss"
                :model-value="form.lines[line.key]?.supplierId ?? null"
                :options="options"
                aria-label="Sous-traitant"
                placeholder="Sous-traitant (fournisseur Axonaut)…"
                :clearable="false"
                empty-text="Aucun fournisseur Axonaut : créez-le dans Axonaut, puis synchronisez"
                :testid="`st-supplier-${line.index}`"
                @update:model-value="(v) => setLineSupplier(line.key, v)"
              />
              <span>payé</span>
              <input
                class="st-amount"
                type="text"
                inputmode="decimal"
                aria-label="Coût payé au sous-traitant"
                :value="costTexts[line.key] ?? ''"
                :data-testid="`st-cost-${line.index}`"
                @input="setLineCost(line.key, ($event.target as HTMLInputElement).value)"
              />
              <span>€ HT</span>
            </template>
            <template v-else>Fait en interne</template>
            <span v-if="line.moved" class="st-warn">(ligne déplacée dans Axonaut, retrouvée)</span>
          </span>
        </div>
        <p v-for="o in dto.orphans" :key="o.label + o.supplierName" class="st-warn" data-testid="st-orphan">
          « {{ o.label }} » ({{ o.supplierName }}, {{ eur(o.costCents) }}) n’existe plus dans le devis Axonaut
          : son coût reste compté jusqu’au prochain enregistrement, qui le retire.
        </p>
      </div>

      <div v-else-if="form.mode === 'global'" class="st-lines" data-testid="st-global">
        <div class="st-line on" style="grid-template-columns: minmax(0, 1fr)">
          <span class="nm">Montant sous-traité sur l’ensemble du devis</span>
          <span class="det" style="grid-column: 1">
            <SearchSelect
              class="ss"
              :model-value="form.global.supplierId"
              :options="options"
              aria-label="Sous-traitant"
              placeholder="Sous-traitant (fournisseur Axonaut)…"
              :clearable="false"
              empty-text="Aucun fournisseur Axonaut : créez-le dans Axonaut, puis synchronisez"
              testid="st-supplier-global"
              @update:model-value="setGlobalSupplier"
            />
            <span>payé</span>
            <input
              class="st-amount"
              type="text"
              inputmode="decimal"
              aria-label="Montant sous-traité"
              :value="globalText"
              data-testid="st-cost-global"
              @input="setGlobalCost(($event.target as HTMLInputElement).value)"
            />
            <span>€ HT</span>
          </span>
        </div>
      </div>

      <p v-else class="muted" data-testid="st-none">
        Tout est fait en interne : le devis compte en entier pour Evolusite.
      </p>

      <div v-if="totals" class="st-split" data-testid="st-split">
        <div class="st-bar" aria-hidden="true">
          <i :style="{ width: barStyle.ev, background: 'var(--accent)' }"></i
          ><i :style="{ width: barStyle.st, background: 'var(--sub)' }"></i>
        </div>
        <div class="r">
          <span>Devis signé</span><span class="num">{{ eur(dto.amountCents) }}</span>
        </div>
        <div class="r">
          <span class="sub-text">Sous-traité</span
          ><span class="num" data-testid="st-subcontracted">{{
            totals.subcontractedCents > 0 ? `− ${eur(totals.subcontractedCents)}` : '0 €'
          }}</span>
        </div>
        <div class="r big">
          <span>Part d’Evolusite</span
          ><span class="num" data-testid="st-evolusite">{{ eur(totals.evolusiteCents) }}</span>
        </div>
        <p v-if="totals.costCents > dto.amountCents" class="st-warn">
          Le coût confié ({{ eur(totals.costCents) }}) dépasse le prix du devis : la part d’Evolusite est
          comptée à 0 €.
        </p>
      </div>

      <div v-if="savedSubcontracted && dto.due.date" class="st-due" data-testid="st-due">
        <b
          >À payer{{ mainSupplier ? ` à ${mainSupplier}` : '' }} au plus tard le {{ frDate(dto.due.date) }}</b
        >
        <span class="muted">{{ dueExplanation(dto.due) }} ·</span>
        <template v-if="!editingDate">
          <button
            class="btn sm"
            type="button"
            :disabled="!online || saving"
            :title="online ? undefined : 'Disponible avec le réseau'"
            data-testid="st-change-date"
            @click="openDate"
          >
            Changer la date
          </button>
          <button
            v-if="dto.due.overridden"
            class="btn sm"
            type="button"
            :disabled="!online || saving"
            @click="saveDate(null)"
          >
            Revenir au calcul
          </button>
        </template>
        <template v-else>
          <input
            v-model="dateInput"
            type="date"
            aria-label="À payer au plus tard le"
            data-testid="st-date-input"
          />
          <button
            class="btn sm primary"
            type="button"
            :disabled="!dateInput || saving"
            data-testid="st-date-save"
            @click="saveDate(dateInput)"
          >
            Enregistrer la date
          </button>
          <button class="btn sm" type="button" @click="editingDate = false">Annuler</button>
        </template>
      </div>

      <p v-if="error" class="form-error" role="alert">{{ error }}</p>
      <div class="st-foot">
        <small>Rien ne part vers Axonaut ni vers le sous-traitant : c’est une information du Cockpit.</small>
        <button
          class="btn primary"
          type="button"
          :disabled="!online || saving"
          :title="online ? undefined : 'Disponible avec le réseau'"
          data-testid="st-save"
          @click="save"
        >
          {{ online ? 'Enregistrer la sous-traitance' : 'Disponible avec le réseau' }}
        </button>
      </div>
    </template>
  </div>
</template>
